import { useCallback, useState } from 'react';
import { AppActionCreators } from '@nkr/store/index';
import { ActionCreators as AuthActionCreators } from '@nkr/store/authorization/actions';
import { toast } from 'react-toastify';
import { downloadFile as saveFile } from '@nkr/services/download-file';
import { useAppHookContext } from '@nkr/utils/rest-api/useAppHookContext';
import type { SearchParams } from '@nkr/utils/rest-api/types';

export function useDownloadApi<TSearch>(
    path: string,
    options?: {
        fileName?: string | ((params: SearchParams) => string);
        mimeType?: string;
        responseType?: 'text' | 'blob';
    }
) {
    const { dispatch } = useAppHookContext();
    const [isDownloading, setIsDownloading] = useState(false);

    const downloadFile = useCallback(async (params: SearchParams = {}) => {
        setIsDownloading(true);
        dispatch(AppActionCreators.showLoadingAsync('Downloading...'));

        try {
            const auth = await dispatch(AuthActionCreators.checkToken());
            const token = auth?.token?.access_token as string | undefined;

            const qs = new URLSearchParams();

            for (const [key, value] of Object.entries(params)) {
                if (Array.isArray(value)) {
                    for (const item of value) {
                        if (item !== undefined && item !== null) {
                            qs.append(key, String(item));
                        }
                    }
                } else if (value !== undefined && value !== null) {
                    qs.append(key, String(value));
                }
            }

            const url = qs.toString() ? `${path}?${qs.toString()}` : path;

            const response = await fetch(url, {
                headers: token ? { Authorization: `Bearer ${token}` } : undefined
            });

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }

            const data = options?.responseType === 'text'
                ? await response.text()
                : await response.blob();

            const fileName = typeof options?.fileName === 'function'
                ? options.fileName(params)
                : (options?.fileName || 'download');

            saveFile(data, fileName, options?.mimeType || 'application/octet-stream');
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Download failed';
            toast.error(message);
        } finally {
            dispatch(AppActionCreators.hideLoading());
            setIsDownloading(false);
        }
    }, [dispatch, path, options]);

    return { downloadFile, isDownloading };
}